import React from "react";
import { createPortal } from "react-dom";
import ReactQuill from "react-quill";
import { X } from "lucide-react";
import { extractPlainText } from "../../utils/exportUtils";
import { handleDownloadPDF, handleDownloadDoc } from "../../utils/exportUtils";
import { useNotification } from "../../context/NotificationContext";
import "react-quill/dist/quill.snow.css";
import "./Dashboard.css";

const DEFAULT_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ color: [] }, { background: [] }],
    ["link"],
    ["clean"],
  ],
};

const DEFAULT_FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "color",
  "background",
  "link",
];


/**
 * EditorModal - Rich text editor modal for editing and exporting content
 * @param {boolean} isOpen - Whether modal is open
 * @param {string} title - Title of the document
 * @param {string} content - HTML content
 * @param {Function} onClose - Callback to close modal
 * @param {Function} onSave - Callback when save is clicked (receives plainText)
 * @param {Object} quillModules - Quill editor modules configuration
 * @param {Array} quillFormats - Quill editor formats
 * @param {boolean} darkMode - Current dark mode state
 */
export default function EditorModal({
  isOpen = false,
  title = "Document Editor",
  content = "",
  onClose,
  onSave,
  quillModules = {},
  quillFormats = [],
  darkMode = false,
}) {
  const { showError } = useNotification();

  const modules = React.useMemo(() => {
    return Object.keys(quillModules).length > 0 ? quillModules : DEFAULT_MODULES;
  }, [quillModules]);

  const formats = React.useMemo(() => {
    return quillFormats.length > 0 ? quillFormats : DEFAULT_FORMATS;
  }, [quillFormats]);
  const [editorContent, setEditorContent] = React.useState(content);
  const [isGeneratingPDF, setIsGeneratingPDF] = React.useState(false);
  const [isGeneratingDoc, setIsGeneratingDoc] = React.useState(false);
  const quillRef = React.useRef(null);

  // Update content when prop changes
  React.useEffect(() => {
    setEditorContent(content);
    // Also set the content in Quill using setContents to ensure HTML is properly parsed
    if (quillRef.current && quillRef.current.getEditor) {
      const editor = quillRef.current.getEditor();
      if (editor && content) {
        // Set HTML directly into Quill
        setTimeout(() => {
          if (editor && editor.root) {
            editor.root.innerHTML = content;
            console.log(
              "[EditorModal] Set HTML content:",
              content.substring(0, 100),
            );
          }
        }, 100); // Small delay to ensure Quill is ready
      }
    }
  }, [content, isOpen]);

  const handleSave = () => {
    const plainText = extractPlainText(editorContent);
    if (onSave) {
      // Pass HTML content as primary argument, plain text as secondary
      onSave(editorContent, plainText);
    }
    if (onClose) {
      onClose();
    }
  };

  const handleDownloadPDFClick = async () => {
    try {
      setIsGeneratingPDF(true);
      await handleDownloadPDF(editorContent, title, showError);
    } catch (error) {
      console.error("PDF download failed:", error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleDownloadDocClick = async () => {
    try {
      setIsGeneratingDoc(true);
      await handleDownloadDoc(editorContent, title, showError);
    } catch (error) {
      console.error("DOCX download failed:", error);
    } finally {
      setIsGeneratingDoc(false);
    }
  };

  if (!isOpen || typeof document === "undefined") return null;

  return createPortal(
    <div
      className={`editor-modal ${darkMode ? "dark" : ""}`}
      role="dialog"
      aria-modal="true"
    >
      <div className="editor-panel">
        <div className="editor-header">
          <div>
            <p className="editor-label">Rich Text Editor</p>
            <h3>{title}</h3>
          </div>
          <button
            className="editor-close-btn"
            onClick={onClose}
            aria-label="Close editor"
          >
            <X size={18} />
          </button>
        </div>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={editorContent}
          onChange={setEditorContent}
          modules={modules}
          formats={formats}
          className="editor-quill"
        />
        <div className="editor-actions">
          <button
            className="editor-action-btn editor-action-primary"
            onClick={handleSave}
          >
            Save Changes
          </button>
          <button
            className="editor-action-btn"
            onClick={handleDownloadPDFClick}
            disabled={isGeneratingPDF}
          >
            {isGeneratingPDF ? "Generating PDF..." : "Download PDF"}
          </button>
          <button
            className="editor-action-btn"
            onClick={handleDownloadDocClick}
            disabled={isGeneratingDoc}
          >
            {isGeneratingDoc ? "Generating DOCX..." : "Download DOCX"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
